import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Checkbox,
  Alert,
  CircularProgress,
  Snackbar,
  Stack,
  Paper,
  Chip,
  InputAdornment,
  Autocomplete,
  Breadcrumbs,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
  SportsKabaddi as CoachIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Group as GroupIcon,
  PersonAdd as PersonAddIcon,
  ExpandMore as ExpandMoreIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Person as PersonIcon,
  ContentCopy as ContentCopyIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { useAuth } from '../../context/AuthContext';
import { coachingSessionAPI, userAPI, amenityAPI } from '../../services/api';
import AdminAccessControl from '../common/AdminAccessControl';
import { CoachingSession, CoachingSessionBooking, CreateCoachingSessionData, RateStructure } from '../../types/coaching';
import { formatTimeString, formatDateForInput, parseDateOnlyString } from '../../utils/dateUtils';
import { validateCoachingTimeSlotAlignment, formatSlotAlignmentMessage, dateToTimeString } from '../../utils/slotValidation';
import { amenityScheduleAPI } from '../../services/api';
import BookCoachingSessionDialog from './BookCoachingSessionDialog';
import SessionBookingsView from './SessionBookingsView';
import ReservationResultsDialog from './ReservationResultsDialog';
import { User } from '../../types';
import { UserRole } from '../../types';

const CoachingSessionManagementPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // State for coaching sessions
  const [coachingSessions, setCoachingSessions] = useState<CoachingSession[]>([]);
  const [coaches, setCoaches] = useState<User[]>([]);
  const [amenities, setAmenities] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [conflictingBookings, setConflictingBookings] = useState<any[]>([]);
  const [savingSession, setSavingSession] = useState<boolean>(false);

  // Reservation result dialog state
  const [reservationDialogOpen, setReservationDialogOpen] = useState(false);
  const [reservationDialogData, setReservationDialogData] = useState<any>(null);

  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openBookingsDialog, setOpenBookingsDialog] = useState(false);
  const [openBookSessionDialog, setOpenBookSessionDialog] = useState(false);
  const [selectedSession, setSelectedSession] = useState<CoachingSession | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Filter states
  const [searchText, setSearchText] = useState<string>('');
  const [coachFilter, setCoachFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('SCHEDULED'); // Default to scheduled sessions
  const [levelFilter, setLevelFilter] = useState<string>('');

  const DEFAULT_SESSION_DURATION = 30; // in minutes
  
  // Form state
  const [formData, setFormData] = useState(() => {
    const defaultStartTime = new Date();
    const defaultEndTime = new Date(defaultStartTime);
    defaultEndTime.setMinutes(defaultStartTime.getMinutes() + DEFAULT_SESSION_DURATION); // Add DEFAULT_SESSION_DURATION in minutes
    
    return {
      name: '',
      description: '',
      coachId: '',
      isGuestCoach: false,
      guestCoachName: '',
      venues: [] as string[],
      level: '',
      capacity: 4,
      sessionDate: new Date(),
      startTime: defaultStartTime,
      endTime: defaultEndTime,
      minBookingDurationMinutes: DEFAULT_SESSION_DURATION,
      memberRate: { cost: '0', duration: '' },
      guestRate: { cost: '0', duration: '' },
      allowGuestBookings: true,
      notes: '',
      image: ''
    };
  });

  // Collapsible section states
  const [pricingConfigExpanded, setPricingConfigExpanded] = useState(false);

  // Slot validation state
  const [slotValidationWarning, setSlotValidationWarning] = useState<string | null>(null);
  const [venueSchedules, setVenueSchedules] = useState<Map<string, any[]>>(new Map());
  const [venueNames, setVenueNames] = useState<Map<string, string>>(new Map());
  const [loadingVenueSchedules, setLoadingVenueSchedules] = useState<boolean>(false);

  // Fetch data on component mount
  useEffect(() => {
    fetchCoachingSessions();
    fetchCoaches();
    fetchAmenities();
  }, []);

  const fetchCoachingSessions = async () => {
    try {
      setLoading(true);
      const response = await coachingSessionAPI.getCoachingSessions();
      setCoachingSessions(response.data);
    } catch (error: any) {
      console.error('Error fetching coaching sessions:', error);
      
      // Extract the error message from the backend response
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to fetch coaching sessions';
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoaches = async () => {
    try {
      const response = await userAPI.getCoaches();
      setCoaches(response.data);
    } catch (error: any) {
      console.error('Error fetching coaches:', error);
      
      // Extract the error message from the backend response
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to fetch coaches';
      
      setError(errorMessage);
    }
  };

  const fetchAmenities = async () => {
    try {
      const response = await amenityAPI.getAmenities();
      setAmenities(response.data);
      
      // Build venue names map
      const names = new Map<string, string>();
      response.data.forEach((amenity: any) => {
        names.set(amenity.id, amenity.name);
      });
      setVenueNames(names);
    } catch (error: any) {
      console.error('Error fetching amenities:', error);
      // Don't set error state for amenities - it's optional
    }
  };

  // Fetch schedules for selected venues
  const fetchVenueSchedules = async (venueIds: string[]) => {
    if (venueIds.length === 0) {
      setVenueSchedules(new Map());
      return;
    }

    try {
      setLoadingVenueSchedules(true);
      const schedules = new Map<string, any[]>();

      // Fetch schedules for each venue
      await Promise.all(
        venueIds.map(async (venueId) => {
          try {
            const response = await amenityScheduleAPI.getAmenitySchedules(venueId);
            schedules.set(venueId, response.data);
          } catch (error) {
            console.error(`Error fetching schedule for venue ${venueId}:`, error);
            schedules.set(venueId, []);
          }
        })
      );

      setVenueSchedules(schedules);
    } catch (error) {
      console.error('Error fetching venue schedules:', error);
    } finally {
      setLoadingVenueSchedules(false);
    }
  };

  // Validate slot alignment when venues or times change
  const validateSlotAlignment = async () => {
    if (formData.venues.length === 0) {
      setSlotValidationWarning(null);
      return;
    }

    const startTimeStr = dateToTimeString(formData.startTime);
    const endTimeStr = dateToTimeString(formData.endTime);

    const result = await validateCoachingTimeSlotAlignment(
      formData.venues,
      venueSchedules,
      venueNames,
      formData.sessionDate,
      startTimeStr,
      endTimeStr
    );

    if (!result.valid) {
      setSlotValidationWarning(formatSlotAlignmentMessage(result));
    } else if (result.warnings.length > 0) {
      setSlotValidationWarning(formatSlotAlignmentMessage(result));
    } else {
      setSlotValidationWarning(null);
    }
  };

  // Effect to fetch venue schedules when venues change
  useEffect(() => {
    if (formData.venues.length > 0) {
      fetchVenueSchedules(formData.venues);
    } else {
      setVenueSchedules(new Map());
      setSlotValidationWarning(null);
    }
  }, [formData.venues]);

  // Effect to validate slot alignment when schedules are loaded or times change
  useEffect(() => {
    if (venueSchedules.size > 0 && formData.venues.length > 0) {
      validateSlotAlignment();
    }
  }, [venueSchedules, formData.startTime, formData.endTime, formData.sessionDate]);

  const handleCreateSession = () => {
    setIsEditing(false);
    setError(null);
    setConflictingBookings([]);
    
    // Initialize start time and calculate end time based on default min booking duration
    const defaultStartTime = new Date();
    const defaultEndTime = new Date(defaultStartTime);
    defaultEndTime.setMinutes(defaultStartTime.getMinutes() + 30); // Add 30 minutes
    
    setFormData({
      name: '',
      description: '',
      coachId: '',
      isGuestCoach: false,
      guestCoachName: '',
      venues: [] as string[],
      level: '',
      capacity: 4,
      sessionDate: new Date(),
      startTime: defaultStartTime,
      endTime: defaultEndTime,
      minBookingDurationMinutes: 30,
      memberRate: { cost: '0', duration: '' },
      guestRate: { cost: '0', duration: '' },
      allowGuestBookings: true,
      notes: '',
      image: ''
    });
    setPricingConfigExpanded(false);
    setOpenCreateDialog(true);
  };

  const handleEditSession = (session: CoachingSession) => {
    // Navigate to dedicated edit page
    navigate(`/coaching/sessions/edit/${session.id}`);
  };

  const handleDeleteSession = (session: CoachingSession) => {
    setSelectedSession(session);
    setOpenDeleteDialog(true);
  };

  const confirmDeleteSession = async () => {
    if (!selectedSession) return;

    try {
      await coachingSessionAPI.deleteCoachingSession(selectedSession.id);
      await fetchCoachingSessions();
      setOpenDeleteDialog(false);
      setSelectedSession(null);
      setConflictingBookings([]);
      setSuccessMessage('Coaching session deleted successfully');
    } catch (error: any) {
      console.error('Error deleting coaching session:', error);
      
      // Check if error contains conflicting bookings information
      if (error.response?.data?.activeBookings) {
        setConflictingBookings(error.response.data.activeBookings);
      } else {
        setConflictingBookings([]);
      }
      
      // Extract the error message from the backend response
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to delete coaching session';
      
      setError(errorMessage);
    }
  };

  const handleViewBookings = (session: CoachingSession) => {
    setSelectedSession(session);
    setOpenBookingsDialog(true);
  };

  const handleBookSession = (session: CoachingSession) => {
    setSelectedSession(session);
    setOpenBookSessionDialog(true);
  };

  const handleCopySession = (session: CoachingSession) => {
    setIsEditing(false);
    setError(null);
    setConflictingBookings([]);
    
    // Parse times from the original session
    const startTime = new Date();
    const endTime = new Date();
    
    // Parse the session's start and end times
    if (session.startTime) {
      const [hours, minutes] = session.startTime.split(':').map(Number);
      startTime.setHours(hours, minutes, 0, 0);
    }
    
    if (session.endTime) {
      const [hours, minutes] = session.endTime.split(':').map(Number);
      endTime.setHours(hours, minutes, 0, 0);
    }
    
    const sessionDateParsed = parseDateOnlyString(session.sessionDate);
    
    // Copy all session data to form
    setFormData({
      name: `${session.name} - Copy`,
      description: session.description || '',
      coachId: session.coachId || '',
      isGuestCoach: session.isGuestCoach || !session.coachId,
      guestCoachName: session.guestCoachName || '',
      venues: session.venues || [],
      level: session.level || '',
      capacity: session.capacity,
      sessionDate: sessionDateParsed,
      startTime,
      endTime,
      minBookingDurationMinutes: session.minBookingDurationMinutes || DEFAULT_SESSION_DURATION,
      memberRate: {
        cost: session.memberRateCost?.toString() || '0',
        duration: session.memberRateDuration?.toString() || ''
      },
      guestRate: {
        cost: session.guestRateCost?.toString() || '0',
        duration: session.guestRateDuration?.toString() || ''
      },
      allowGuestBookings: session.allowGuestBookings ?? true,
      notes: session.notes || '',
      image: session.image || ''
    });
    
    // Expand pricing configuration if there are rates configured
    setPricingConfigExpanded(!!(session.memberRateCost || session.guestRateCost));
    
    setOpenCreateDialog(true);
  };

  const handleBookingSuccess = (message?: string) => {
    setOpenBookSessionDialog(false);
    setSelectedSession(null);
    if (message) {
      setSuccessMessage(message);
    }
    // Refresh sessions to update booking counts
    fetchCoachingSessions();
  };

  const handleSubmit = async () => {
    setError(null);
    
    // Validation
    if (!formData.name.trim()) {
      setError('Session name is required');
      return;
    }
    
    if (!formData.isGuestCoach && !formData.coachId) {
      setError('Please select a coach or enable guest coach option');
      return;
    }
    
    if (formData.isGuestCoach && !formData.guestCoachName.trim()) {
      setError('Guest coach name is required');
      return;
    }
    
    // Validate time logic
    if (formData.startTime >= formData.endTime) {
      setError('End time must be after start time');
      return;
    }

    try {
      setSavingSession(true);
      const startTime = formData.startTime.toTimeString().split(' ')[0].slice(0, 5);
      const endTime = formData.endTime.toTimeString().split(' ')[0].slice(0, 5);

      const sessionData: CreateCoachingSessionData = {
        name: formData.name,
        description: formData.description,
        coachId: formData.isGuestCoach ? null : formData.coachId,
        isGuestCoach: formData.isGuestCoach,
        guestCoachName: formData.isGuestCoach ? formData.guestCoachName : undefined,
        venues: formData.venues,
        level: formData.level,
        capacity: formData.capacity,
        sessionDate: formatDateForInput(formData.sessionDate),
        startTime,
        endTime,
        minBookingDurationMinutes: formData.minBookingDurationMinutes,
        memberRate: formData.memberRate.cost !== '' && formData.memberRate.cost !== null && formData.memberRate.cost !== undefined ? {
          cost: parseFloat(formData.memberRate.cost),
          duration: parseInt(formData.memberRate.duration) || formData.minBookingDurationMinutes
        } : undefined,
        guestRate: formData.guestRate.cost !== '' && formData.guestRate.cost !== null && formData.guestRate.cost !== undefined ? {
          cost: parseFloat(formData.guestRate.cost),
          duration: parseInt(formData.guestRate.duration) || formData.minBookingDurationMinutes
        } : undefined,
        allowGuestBookings: formData.allowGuestBookings,
        notes: formData.notes,
        image: formData.image
      };

      const operationType = isEditing ? 'update' : 'create';
      const response = isEditing && selectedSession
        ? await coachingSessionAPI.updateCoachingSession(selectedSession.id, sessionData)
        : await coachingSessionAPI.createCoachingSession(sessionData);

      // Close dialog and refresh list
      setOpenCreateDialog(false);
      setConflictingBookings([]);
      await fetchCoachingSessions();

      // Show reservation results dialog
      if (response.data.reservationResult) {
        setReservationDialogData({
          entityType: 'session',
          entityName: formData.name,
          operationType,
          reservationResult: response.data.reservationResult
        });
        setReservationDialogOpen(true);
      } else {
        setSuccessMessage(`Coaching session ${operationType}d successfully`);
      }
    } catch (error: any) {
      console.error('Error saving coaching session:', error);
      
      // Check if error contains conflicting bookings information
      if (error.response?.data?.conflictingBookings) {
        setConflictingBookings(error.response.data.conflictingBookings);
      } else {
        setConflictingBookings([]);
      }
      
      // Extract the error message from the backend response
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          (isEditing ? 'Failed to update coaching session' : 'Failed to create coaching session');
      
      setError(errorMessage);
    } finally {
      setSavingSession(false);
    }
  };

  const formatTime = (timeString: string) => {
    return formatTimeString(timeString);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toISOString().split('T')[0];
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SCHEDULED':
        return 'primary';
      case 'ACTIVE':
        return 'success';
      case 'COMPLETED':
        return 'default';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  // Filter sessions based on search and filter criteria
  const filteredSessions = useMemo(() => {
    let filtered = [...coachingSessions];

    // Text search (session name, description, coach name, guest coach name, level)
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(session =>
        session.name.toLowerCase().includes(searchLower) ||
        (session.description && session.description.toLowerCase().includes(searchLower)) ||
        (session.coachName && session.coachName.toLowerCase().includes(searchLower)) ||
        (session.guestCoachName && session.guestCoachName.toLowerCase().includes(searchLower)) ||
        (session.level && session.level.toLowerCase().includes(searchLower))
      );
    }

    // Coach filter
    if (coachFilter) {
      if (coachFilter === 'guest') {
        // Show guest coaches (those without a coachId or where isGuestCoach is true)
        filtered = filtered.filter(session => !session.coachId || session.isGuestCoach);
      } else {
        // Show specific coach
        filtered = filtered.filter(session => session.coachId === coachFilter);
      }
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(session => session.status === statusFilter);
    }
    // If statusFilter is empty string, show all

    // Level filter
    if (levelFilter) {
      filtered = filtered.filter(session => session.level === levelFilter);
    }

    return filtered;
  }, [coachingSessions, searchText, coachFilter, statusFilter, levelFilter]);

  // Clear all filters
  const clearFilters = () => {
    setSearchText('');
    setCoachFilter('');
    setStatusFilter('SCHEDULED'); // Reset to default
    setLevelFilter('');
  };

  // Get unique levels from sessions
  const getUniqueLevels = () => {
    const levels = coachingSessions
      .map(session => session.level)
      .filter(level => level && level.trim() !== '');
    return [...new Set(levels)].sort();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg">
        <AdminAccessControl allowedRoles={[UserRole.COACH, UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.EMPLOYEE]}>
        <Box sx={{ mt: 4, mb: 4 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Button
              component={RouterLink}
              to="/dashboard"
              color="inherit"
              startIcon={<HomeIcon />}
              sx={{ textTransform: 'none' }}
            >
              Dashboard
            </Button>
            <Typography color="text.primary">Coaching Session Management</Typography>
          </Breadcrumbs>

          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              <CoachIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
              Coaching Session Management
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchCoachingSessions}
                disabled={loading}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateSession}
                size="large"
              >
                Create Session
              </Button>
            </Stack>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Success Snackbar */}
          <Snackbar
            open={!!successMessage}
            autoHideDuration={4000}
            onClose={() => setSuccessMessage(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={() => setSuccessMessage(null)} severity="success">
              {successMessage}
            </Alert>
          </Snackbar>

          {/* Filters */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  placeholder="Search by session name, description, coach, or level..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  size="small"
                  sx={{ minWidth: 300 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Coach</InputLabel>
                  <Select
                    value={coachFilter}
                    onChange={(e: SelectChangeEvent) => setCoachFilter(e.target.value)}
                    label="Coach"
                  >
                    <MenuItem value="">All Coaches</MenuItem>
                    <MenuItem value="guest">Guest Coaches</MenuItem>
                    {coaches.map((coach) => (
                      <MenuItem key={coach.id} value={coach.id}>
                        {coach.firstName} {coach.lastName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                    <MenuItem value="COMPLETED">Completed</MenuItem>
                    <MenuItem value="CANCELLED">Cancelled</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Level</InputLabel>
                  <Select
                    value={levelFilter}
                    onChange={(e: SelectChangeEvent) => setLevelFilter(e.target.value)}
                    label="Level"
                  >
                    <MenuItem value="">All Levels</MenuItem>
                    {getUniqueLevels().map((level) => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  startIcon={<ClearIcon />}
                  size="small"
                >
                  Clear Filters
                </Button>
              </Box>
            </Stack>
          </Paper>

          {/* Coaching Sessions Grid */}
          <Grid container spacing={3}>
            {filteredSessions.map((session) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={session.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography 
                        variant="h6" 
                        component="h2" 
                        noWrap
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { color: 'primary.main', textDecoration: 'underline' }
                        }}
                        onClick={() => handleEditSession(session)}
                        title="Click to edit session"
                      >
                        {session.name}
                      </Typography>
                      <Chip
                        label={session.status}
                        color={getStatusColor(session.status)}
                        size="small"
                      />
                    </Box>
                    
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          Coach: {session.coachName}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {Array.isArray(session.venues) && session.venues.length > 0 
                            ? `${session.venues.length} venue(s)` 
                            : 'No venues'} • {session.level}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TimeIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(session.sessionDate)} • {formatTime(session.startTime)} - {formatTime(session.endTime)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleViewBookings(session)}>
                        <GroupIcon sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2" color="primary.main" sx={{ textDecoration: 'underline' }}>
                          Capacity: {session.capacity} • Min booking: {session.minBookingDurationMinutes} min
                        </Typography>
                      </Box>

                      {(session.memberRateCost || session.guestRateCost) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MoneyIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {session.memberRateCost && `$${session.memberRateCost}/${session.memberRateDuration}min`}
                            {session.memberRateCost && session.guestRateCost && ' • '}
                            {session.guestRateCost && `Guest: $${session.guestRateCost}/${session.guestRateDuration}min`}
                          </Typography>
                        </Box>
                      )}

                      {session.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {session.description}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                  
                  <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => handleCopySession(session)}
                      title="Copy session to create a new one"
                    >
                      Copy
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<PersonAddIcon />}
                      onClick={() => handleBookSession(session)}
                    >
                      Enroll
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {coachingSessions.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <CoachIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No coaching sessions found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create your first coaching session to get started
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateSession}>
                Create Session
              </Button>
            </Paper>
          ) : filteredSessions.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <CoachIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No sessions match your filters
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Try adjusting your search criteria or clearing filters
              </Typography>
              <Button variant="outlined" startIcon={<ClearIcon />} onClick={clearFilters}>
                Clear Filters
              </Button>
            </Paper>
          )}
        </Box>

        {/* Create/Edit Dialog */}
        <Dialog 
          open={openCreateDialog}
          disableEscapeKeyDown={true}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {isEditing ? 'Edit Coaching Session' : 'Create New Coaching Session'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Session Name */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Session Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>

              {/* Coach Selection */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={2}>
                  <Autocomplete
                    options={coaches}
                    getOptionLabel={(option: User | string) => {
                      if (typeof option === 'string') return option;
                      if (!option) return '';
                      const firstName = option.firstName || '';
                      const lastName = option.lastName || '';
                      const fullName = `${firstName} ${lastName}`.trim();
                      if (fullName) return fullName;
                      return option.email || 'Unknown Coach';
                    }}
                    value={coaches.find(coach => coach.id === formData.coachId) || null}
                    onChange={(event, newValue) => {
                      setFormData({ 
                        ...formData, 
                        coachId: newValue?.id || '',
                        guestCoachName: newValue ? (newValue.firstName && newValue.lastName ? `${newValue.firstName} ${newValue.lastName}` : newValue.email || 'Unknown Coach') : formData.guestCoachName
                      });
                    }}
                    disabled={formData.isGuestCoach}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Coach"
                        required={!formData.isGuestCoach}
                      />
                    )}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isGuestCoach}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          isGuestCoach: e.target.checked,
                          coachId: e.target.checked ? '' : formData.coachId
                        })}
                      />
                    }
                    label="Guest Coach/Instructor"
                  />
                  {formData.isGuestCoach && (
                    <TextField
                      label="Guest Coach Name"
                      value={formData.guestCoachName}
                      onChange={(e) => setFormData({ ...formData, guestCoachName: e.target.value })}
                      fullWidth
                      required
                      placeholder="Enter coach/instructor name"
                    />
                  )}
                </Stack>
              </Grid>

              {/* Venues */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  multiple
                  options={amenities}
                  getOptionLabel={(option) => `${option.name} (${option.type})`}
                  getOptionKey={(option) => option.id}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={amenities.filter(a => formData.venues.includes(a.id))}
                  onChange={(_, newValue) => {
                    setFormData({ ...formData, venues: newValue.map(a => a.id) });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Venues"
                      placeholder="Select one or more venues/courts (optional)"
                      helperText="Leave empty if no venue reservation needed"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...chipProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={option.id}
                          label={option.name}
                          {...chipProps}
                          size="small"
                        />
                      );
                    })
                  }
                />
              </Grid>

              {/* Slot Alignment Warning */}
              {slotValidationWarning && (
                <Grid size={{ xs: 12 }}>
                  <Alert 
                    severity={slotValidationWarning.includes('❌') ? 'error' : 'warning'}
                    sx={{ whiteSpace: 'pre-line' }}
                  >
                    <strong>Venue Slot Alignment:</strong>
                    {loadingVenueSchedules ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2">Checking venue schedules...</Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                        {slotValidationWarning}
                      </Typography>
                    )}
                  </Alert>
                </Grid>
              )}

              {/* Level */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Level"
                  value={formData.level}
                  onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                  fullWidth
                  placeholder="e.g., Beginner, Intermediate, Advanced"
                />
              </Grid>

              {/* Session Date */}
              <Grid size={{ xs: 12, md: 6 }}>
                <DatePicker
                  label="Session Date"
                  value={formData.sessionDate}
                  onChange={(date) => setFormData({ ...formData, sessionDate: date || new Date() })}
                  slotProps={{ 
                    textField: { 
                      fullWidth: true, 
                      required: true,
                      helperText: "Cannot schedule sessions in the past"
                    } 
                  }}
                />
              </Grid>

              {/* Capacity */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 1 })}
                  fullWidth
                  required
                  inputProps={{ min: 1, max: 20 }}
                />
              </Grid>

              {/* Start Time */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TimePicker
                  label="Start Time"
                  value={formData.startTime}
                  onChange={(time) => {
                    let newEndTime = new Date(time?.toDateString() || '');
                    newEndTime.setHours(time?.getHours() || 0);
                    newEndTime.setMinutes((time?.getMinutes() || 0) + formData.minBookingDurationMinutes);
                    setFormData({ ...formData, 
                      startTime: time || new Date(), 
                      endTime: newEndTime 
                    })}
                }
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>

              {/* End Time */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TimePicker
                  label="End Time"
                  value={formData.endTime}
                  onChange={(time) => setFormData({ ...formData, endTime: time || new Date() })}
                  slotProps={{ textField: { fullWidth: true, required: true } }}
                />
              </Grid>

              {/* Min Booking Duration */}
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Minimum Booking Duration (minutes)"
                  type="number"
                  value={formData.minBookingDurationMinutes}
                  onChange={(e) => {
                    const newDuration = parseInt(e.target.value) || 30;
                    setFormData({ 
                      ...formData, 
                      endTime: new Date(formData.startTime.getTime() + newDuration * 60000),
                      minBookingDurationMinutes: newDuration,
                      // Update rate durations if they're empty or match the previous min duration
                      memberRate: {
                        ...formData.memberRate,
                        duration: !formData.memberRate.duration || formData.memberRate.duration === formData.minBookingDurationMinutes.toString() 
                          ? newDuration.toString() 
                          : formData.memberRate.duration
                      },
                      guestRate: {
                        ...formData.guestRate,
                        duration: !formData.guestRate.duration || formData.guestRate.duration === formData.minBookingDurationMinutes.toString() 
                          ? newDuration.toString() 
                          : formData.guestRate.duration
                      }
                    });
                  }}
                  fullWidth
                  inputProps={{ min: 15, max: 240 }}
                  helperText="Minimum time slot members can book"
                />
              </Grid>

              {/* Allow Guest Bookings */}
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allowGuestBookings}
                      onChange={(e) => setFormData({ ...formData, allowGuestBookings: e.target.checked })}
                    />
                  }
                  label="Allow Guest Bookings"
                  sx={{ mt: 2 }}
                />
              </Grid>

              {/* Description */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>

            {/* Collapsible Pricing Configuration */}
            <Accordion expanded={pricingConfigExpanded} onChange={() => setPricingConfigExpanded(!pricingConfigExpanded)} sx={{ mt: 3 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Pricing Configuration</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {/* Member Rate */}
                  <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Member Rate</Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          label="Cost"
                          type="number"
                          value={formData.memberRate.cost}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            memberRate: { ...formData.memberRate, cost: e.target.value }
                          })}
                          fullWidth
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                          inputProps={{ min: 0, step: 0.01 }}
                          helperText="Cost for members"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          label="Duration (minutes)"
                          type="number"
                          value={formData.memberRate.duration}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            memberRate: { ...formData.memberRate, duration: e.target.value }
                          })}
                          fullWidth
                          inputProps={{ min: 15, max: 480 }}
                          helperText={`Default: ${formData.minBookingDurationMinutes} min`}
                          placeholder={formData.minBookingDurationMinutes.toString()}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Guest Rate */}
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" gutterBottom>Guest Rate</Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          label="Cost"
                          type="number"
                          value={formData.guestRate.cost}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            guestRate: { ...formData.guestRate, cost: e.target.value }
                          })}
                          fullWidth
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                          inputProps={{ min: 0, step: 0.01 }}
                          helperText="Cost for guests"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          label="Duration (minutes)"
                          type="number"
                          value={formData.guestRate.duration}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            guestRate: { ...formData.guestRate, duration: e.target.value }
                          })}
                          fullWidth
                          inputProps={{ min: 15, max: 480 }}
                          helperText={`Default: ${formData.minBookingDurationMinutes} min`}
                          placeholder={formData.minBookingDurationMinutes.toString()}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Notes */}
            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
              sx={{ mt: 2 }}
              placeholder="Internal notes about this session"
            />
          </DialogContent>
          <DialogActions sx={{ flexDirection: 'column', gap: 2, alignItems: 'stretch' }}>
            {/* Error Alert inside Dialog */}
            {error && (
              <Alert severity="error" sx={{ width: '100%' }}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {error}
                  </Typography>
                  
                  {conflictingBookings.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Affected bookings:
                      </Typography>
                      {conflictingBookings.map((booking, index) => (
                        <Box key={index} sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          py: 0.5,
                          px: 1,
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          borderRadius: 1,
                          mb: 0.5
                        }}>
                          <Typography variant="body2">
                            {booking.name} {booking.isGuest && '(Guest)'}
                          </Typography>
                          <Typography variant="caption">
                            {booking.startTime} - {booking.endTime}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Alert>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <Box>
                {isEditing && (
                  <Button 
                    onClick={() => {
                      setOpenCreateDialog(false);
                      setOpenDeleteDialog(true);
                    }}
                    color="error" 
                    startIcon={<DeleteIcon />}
                  >
                    Delete
                  </Button>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={() => setOpenCreateDialog(false)} disabled={savingSession}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={savingSession}>
                  {savingSession ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      {isEditing ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    isEditing ? 'Update' : 'Create'
                  )}
                </Button>
              </Box>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={openDeleteDialog} disableEscapeKeyDown={true}>
          <DialogTitle>Delete Coaching Session</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete "{selectedSession?.name}"? This action cannot be undone.
            </DialogContentText>
            
            {/* Error Alert in Delete Dialog */}
            {error && (
              <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {error}
                  </Typography>
                  
                  {conflictingBookings.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Active bookings that need to be cancelled first:
                      </Typography>
                      {conflictingBookings.map((booking, index) => (
                        <Box key={index} sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          py: 0.5,
                          px: 1,
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          borderRadius: 1,
                          mb: 0.5
                        }}>
                          <Typography variant="body2">
                            {booking.name} {booking.isGuest && '(Guest)'}
                          </Typography>
                          <Typography variant="caption">
                            {booking.startTime} - {booking.endTime}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOpenDeleteDialog(false);
              setConflictingBookings([]);
              setError(null);
            }}>Cancel</Button>
            <Button onClick={confirmDeleteSession} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Book Session Dialog */}
        <BookCoachingSessionDialog
          open={openBookSessionDialog}
          onClose={() => setOpenBookSessionDialog(false)}
          session={selectedSession}
          onSuccess={handleBookingSuccess}
        />

        {/* Session Bookings Dialog */}
        {openBookingsDialog && selectedSession && (
          <Dialog 
            open={openBookingsDialog}
            disableEscapeKeyDown={true}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle>
              <Box>
                <Typography variant="h6" component="span">
                  Session Bookings - {selectedSession.name}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                  {formatDate(selectedSession.sessionDate)} • {formatTime(selectedSession.startTime)} - {formatTime(selectedSession.endTime)}
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <SessionBookingsView 
                session={selectedSession}
                onBookingUpdated={() => {
                  // Refresh the coaching sessions to update booking counts
                  fetchCoachingSessions();
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenBookingsDialog(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Reservation Results Dialog */}
        <ReservationResultsDialog
          open={reservationDialogOpen}
          onClose={() => {
            setReservationDialogOpen(false);
            setReservationDialogData(null);
          }}
          entityType={reservationDialogData?.entityType || 'session'}
          entityName={reservationDialogData?.entityName || ''}
          operationType={reservationDialogData?.operationType || 'create'}
          reservationResult={reservationDialogData?.reservationResult || null}
        />
        </AdminAccessControl>
      </Container>
    </LocalizationProvider>
  );
};

export default CoachingSessionManagementPage;
